<script setup lang="ts">
import type { AnalysisResult } from "@/types/services/analysis/analysis.type.ts";
import type { PricingOption } from "@/types/views/captureView.type";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const props = defineProps<{
  analysis: AnalysisResult;
  selectedOption: PricingOption | null;
  disabled?: boolean;
}>();

defineEmits(["update:selectedOption", "confirmPricing"]);

const costForfait = computed(() => {
  const price = props.analysis.analysis.cout_forfait;
  if (price == null) return "—";

  return price.toLocaleString("fr-FR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
  });
});

const costBuyback = computed(() => {
  const price = props.analysis.analysis.buy_price;
  if (price == null) return "—";

  return price.toLocaleString("fr-FR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
  });
});

const showForfait = computed(() => {
  return props.analysis.analysis.type_forfait === "FORFAIT";
});

const showDevis = computed(() => {
  return (
    props.analysis.analysis.type_forfait === "FORFAIT" ||
    props.analysis.analysis.type_forfait === "DEVIS"
  );
});

const showBuyback = computed(() => {
  return props.analysis.analysis.buy_price != null;
});
</script>

<template>
  <div class="pricing-sheet">
    <h2 class="title">{{ t("views.captureView.pricing.title") }}</h2>
    <div
      v-if="showForfait"
      class="pricing-option"
      :class="{ selected: selectedOption === 'package' }"
      @click="$emit('update:selectedOption', 'package')"
    >
      <div class="radio" />

      <div class="content">
        <div class="label d-flex flex-column align-start">
          <h4>
            {{ t("views.captureView.pricing.packageTitle") }}
          </h4>
          <span class="subtitle">
            {{ t("views.captureView.pricing.packageSub") }}
          </span>
        </div>
        <div class="price">{{ costForfait }}</div>
      </div>
    </div>

    <div
      v-if="showDevis"
      class="pricing-option"
      :class="{ selected: selectedOption === 'estimate' }"
      @click="$emit('update:selectedOption', 'estimate')"
    >
      <div class="radio" />

      <div class="content">
        <div class="label d-flex flex-column align-start">
          <h4>
            {{ t("views.captureView.pricing.quoteTitle") }}
          </h4>
          <span class="subtitle">
            {{ t("views.captureView.pricing.quoteSub") }}
          </span>
        </div>
        <div class="price">—</div>
      </div>
    </div>

    <div
      v-if="showBuyback"
      class="pricing-option"
      :class="{ selected: selectedOption === 'buyback' }"
      @click="$emit('update:selectedOption', 'buyback')"
    >
      <div class="radio" />

      <div class="content">
        <div class="label d-flex flex-column align-start">
          <h4>
            {{ t("views.captureView.pricing.buybackTitle") }}
          </h4>
          <span class="subtitle">
            {{ t("views.captureView.pricing.buybackSub") }}
          </span>
        </div>
        <div class="price">{{ costBuyback }}</div>
      </div>
    </div>

    <v-btn
      block
      class="btn-primary btn-pill mt-6"
      :disabled="disabled || !selectedOption"
      @click="selectedOption && $emit('confirmPricing')"
    >
      {{ t("views.captureView.pricing.confirm") }}
    </v-btn>
  </div>
</template>

<style>
.pricing-sheet {
  padding-bottom: 8px;
}

.title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
}

/* OPTION CARD */
.pricing-option {
  display: flex;
  align-items: center;
  gap: 12px;

  padding: 35px 20px !important;
  border-radius: 20px !important;
  border: 2px solid #e0e0e0;
  background: #fff;

  cursor: pointer;
  transition:
    border 0.2s,
    background 0.2s;
  margin-bottom: 12px;
}

.pricing-option.selected {
  border-color: #559a1b;
  background: #f4fbec;
}

.radio {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid #bdbdbd;
  position: relative;
}

.pricing-option.selected .radio {
  border-color: #559a1b;
}

.pricing-option.selected .radio::after {
  content: "";
  position: absolute;
  inset: 4px;
  background: #559a1b;
  border-radius: 50%;
}

.content {
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.label {
  display: flex;
  align-items: center;
  font-weight: 500;
}

.price {
  padding-left: 30px;
  font-size: 20px;
  font-weight: 600;
  color: #559a1b;
}

/* Radio */
.radio {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid #bdbdbd;
  margin-top: 4px;
  position: relative;
}

.pricing-card.selected .radio {
  border-color: #559a1b;
}

.pricing-card.selected .radio::after {
  content: "";
  position: absolute;
  inset: 5px;
  background: #559a1b;
  border-radius: 50%;
}

.pricing-option {
  position: relative;
}

.subtitle {
  font-size: 14px;
  color: #6b7280;
}
</style>
